import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

export interface BookingEmailData {
  bookingId: string;
  customerEmail: string;
  customerName: string;
  activityName: string;
  date: string;
  timeSlots: string[];
  totalSlots: number;
  totalCost: number;
  currency: string;
}

@Injectable({
  providedIn: 'root'
})
export class EmailService {
  
  /**
   * Get the email function URL from environment or amplify_outputs.json
   */
  private async getEmailFunctionUrl(): Promise<string> {
    // Check environment variable first
    const envUrl = (window as any).__ENV__?.emailFunctionUrl || '';
    if (envUrl) {
      console.log('[EMAIL] Using function URL from environment:', envUrl);
      return envUrl;
    }

    // Try to load from amplify_outputs.json
    try {
      const response = await fetch('/amplify_outputs.json');
      const outputs = await response.json();
      
      // Check various possible paths for the function URL
      const functionUrl = 
        outputs?.custom?.emailFunctionUrl ||
        outputs?.functions?.emailFunction?.urls?.[0] ||
        outputs?.functions?.emailFunction?.url ||
        outputs?.functions?.email?.urls?.[0] ||
        outputs?.functions?.email?.url;
      
      console.log('[EMAIL] Checking amplify_outputs.json structure:', {
        custom: outputs?.custom,
        functions: outputs?.functions
      });
      
      if (functionUrl) {
        console.log('[EMAIL] Found function URL in amplify_outputs.json:', functionUrl);
        return functionUrl;
      }
    } catch (error) {
      console.warn('[EMAIL] Failed to load amplify_outputs.json:', error);
    }

    throw new Error('Email function URL not configured. Please deploy the Amplify email function first.');
  }

  /**
   * Send booking confirmation email
   */
  sendBookingConfirmationEmail(data: BookingEmailData): Observable<any> {
    return from(this.getEmailFunctionUrl()).pipe(
      switchMap(url => {
        return from(
          fetch(`${url}/send-booking-confirmation`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          }).then(async (response) => {
            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Email service error: ${response.status} ${errorText}`);
            }
            return response.json();
          })
        );
      }),
      catchError(error => {
        console.error('[EMAIL] Error sending booking confirmation email:', error);
        return throwError(() => new Error(`Failed to send email: ${error.message}`));
      })
    );
  }
}

